import {
  RevisionSchema,
  SongDocumentSchema,
  createChildRevision,
  hasErrors,
  needsReview,
  patchPractice,
  sameContent,
  validateContent,
  type Arrangement,
  type Practice,
  type PracticePatch,
  type PracticeStatus,
  type Revision,
  type RevisionContent,
  type Song,
  type SongDocument,
} from '../domain'
import type { AtrilDb, EditorDraft } from './db'
import { newId } from './ids'

/**
 * Todo lo que la app lee y escribe de la base de datos. Cada escritura va en una transacción y
 * se valida antes de tocar nada: un fallo no deja una canción a medias.
 */
export class StorageError extends Error {
  code: string
  constructor(code: string, message: string) {
    super(message)
    this.name = 'StorageError'
    this.code = code
  }
}

const millis = (iso: string) => Date.parse(iso)

/** Anota que hay cambios nuevos: es lo que usa el recordatorio de copia de seguridad. */
async function touch(db: AtrilDb, now: string): Promise<void> {
  await db.meta.put({ key: 'lastChangeAt', value: millis(now) })
  const first = await db.meta.get('firstDataAt')
  if (!first || first.value === null) await db.meta.put({ key: 'firstDataAt', value: millis(now) })
}

export async function getMeta(db: AtrilDb, key: string): Promise<number | string | null> {
  return (await db.meta.get(key))?.value ?? null
}

export async function setMeta(
  db: AtrilDb,
  key: string,
  value: number | string | null,
): Promise<void> {
  await db.meta.put({ key, value })
}

export interface SongListItem {
  songId: string
  arrangementId: string
  title: string
  artist: string
  shapeKey?: string
  capo: number
  bpm?: number
  /** Fecha de la revisión principal: cuándo se tocó por última vez la ficha. */
  updatedAt: string
  needsReview: boolean
  /** Estado de práctica: aprendiendo, para pulir o dominada. */
  status?: PracticeStatus
}

/** El repertorio: una entrada por canción, con los datos de su arreglo principal. */
export async function listSongs(db: AtrilDb): Promise<SongListItem[]> {
  const [songs, arrangements, practice] = await Promise.all([
    db.songs.toArray(),
    db.arrangements.toArray(),
    db.practice.toArray(),
  ])
  const statusOf = new Map(practice.map((item) => [item.arrangementId, item.status]))
  const first = new Map<string, Arrangement>()
  for (const arrangement of [...arrangements].sort((a, b) =>
    a.createdAt.localeCompare(b.createdAt),
  )) {
    if (!first.has(arrangement.songId)) first.set(arrangement.songId, arrangement)
  }
  const revisions = await db.revisions.bulkGet(
    [...first.values()].map((item) => item.mainRevisionId),
  )
  const byId = new Map(revisions.flatMap((revision) => (revision ? [[revision.id, revision]] : [])))

  return songs.flatMap((song) => {
    const arrangement = first.get(song.id)
    const revision = arrangement ? byId.get(arrangement.mainRevisionId) : undefined
    if (!arrangement || !revision) return []
    const item: SongListItem = {
      songId: song.id,
      arrangementId: arrangement.id,
      title: song.title,
      artist: song.artist,
      capo: revision.content.capo,
      updatedAt: revision.createdAt,
      needsReview: needsReview(revision.content),
    }
    if (revision.content.shapeKey) item.shapeKey = revision.content.shapeKey
    const bpm = revision.content.tempo.reference?.bpm
    if (bpm !== undefined) item.bpm = bpm
    const status = statusOf.get(arrangement.id)
    if (status) item.status = status
    return [item]
  })
}

/** La ficha de un arreglo con su revisión principal. */
export async function getSongDocument(
  db: AtrilDb,
  arrangementId: string,
): Promise<SongDocument | undefined> {
  const arrangement = await db.arrangements.get(arrangementId)
  if (!arrangement) return undefined
  const [song, revision] = await Promise.all([
    db.songs.get(arrangement.songId),
    db.revisions.get(arrangement.mainRevisionId),
  ])
  if (!song || !revision) return undefined
  return { schemaVersion: 1, song, arrangement, revision }
}

export async function listRevisions(db: AtrilDb, arrangementId: string): Promise<Revision[]> {
  const revisions = await db.revisions.where('arrangementId').equals(arrangementId).toArray()
  return revisions.sort((a, b) => b.createdAt.localeCompare(a.createdAt))
}

function assertValid(revision: Revision): void {
  const parsed = RevisionSchema.safeParse(revision)
  if (!parsed.success) {
    throw new StorageError(
      'invalid',
      `La revisión no cumple el esquema: ${parsed.error.issues[0]?.message}`,
    )
  }
  const issues = validateContent(revision.content)
  if (hasErrors(issues)) {
    const first = issues.find((issue) => issue.severity === 'error')
    throw new StorageError('invalid', `La ficha no es válida: ${first?.message}`)
  }
}

/** Guarda una canción nueva con su arreglo y su primera revisión. */
export async function createSong(db: AtrilDb, document: SongDocument, now: string): Promise<void> {
  const parsed = SongDocumentSchema.safeParse(document)
  if (!parsed.success) {
    throw new StorageError(
      'invalid',
      `La ficha no cumple el esquema: ${parsed.error.issues[0]?.message}`,
    )
  }
  assertValid(document.revision)
  await db.transaction('rw', [db.songs, db.arrangements, db.revisions, db.meta], async () => {
    if (await db.songs.get(document.song.id)) {
      throw new StorageError('exists', 'Ya existe una canción con ese ID')
    }
    await db.songs.add(document.song)
    await db.arrangements.add(document.arrangement)
    await db.revisions.add(document.revision)
    await touch(db, now)
  })
}

export interface SaveRevisionOptions {
  now: string
  author?: Revision['author']
  reason?: string
  /** Solo para pruebas: por defecto se genera uno nuevo. */
  id?: string
}

export interface SavedRevision {
  revision: Revision
  /** Falso si el contenido era idéntico al de la principal y no hizo falta crear nada. */
  created: boolean
}

/**
 * Guarda una edición: crea una revisión hija de la principal y mueve el puntero a ella. Si no
 * cambió nada, no crea nada. La revisión anterior no se toca nunca.
 */
export async function saveRevision(
  db: AtrilDb,
  arrangementId: string,
  content: RevisionContent,
  options: SaveRevisionOptions,
): Promise<SavedRevision> {
  return db.transaction('rw', [db.arrangements, db.revisions, db.meta], async () => {
    const arrangement = await db.arrangements.get(arrangementId)
    if (!arrangement) throw new StorageError('missing', 'El arreglo no existe')
    const parent = await db.revisions.get(arrangement.mainRevisionId)
    if (!parent) throw new StorageError('missing', 'La revisión principal no existe')
    if (sameContent(parent.content, content)) return { revision: parent, created: false }

    const input = {
      id: options.id ?? newId('rev'),
      now: options.now,
      author: options.author ?? 'user',
      ...(options.reason ? { reason: options.reason } : {}),
    }
    const child = createChildRevision(parent, content, input)
    assertValid(child)
    await db.revisions.add(child)
    await db.arrangements.update(arrangementId, { mainRevisionId: child.id })
    await touch(db, options.now)
    return { revision: child, created: true }
  })
}

/** Mueve el puntero a otra revisión del mismo arreglo. Lo único mutable (§3.1). */
export async function setMainRevision(
  db: AtrilDb,
  arrangementId: string,
  revisionId: string,
  now: string,
): Promise<void> {
  await db.transaction('rw', [db.arrangements, db.revisions, db.meta], async () => {
    const revision = await db.revisions.get(revisionId)
    if (!revision || revision.arrangementId !== arrangementId) {
      throw new StorageError('missing', 'Esa revisión no es de este arreglo')
    }
    await db.arrangements.update(arrangementId, { mainRevisionId: revisionId })
    await touch(db, now)
  })
}

export async function updateSong(
  db: AtrilDb,
  songId: string,
  patch: Partial<Pick<Song, 'title' | 'artist' | 'links' | 'references'>>,
  now: string,
): Promise<void> {
  await db.transaction('rw', [db.songs, db.meta], async () => {
    const song = await db.songs.get(songId)
    if (!song) throw new StorageError('missing', 'La canción no existe')
    const next: Song = { ...song, ...patch }
    if (next.title.trim() === '') throw new StorageError('invalid', 'La canción necesita un título')
    await db.songs.put(next)
    await touch(db, now)
  })
}

/** Borra la canción con todos sus arreglos, revisiones y borradores. No tiene vuelta atrás. */
export async function deleteSong(db: AtrilDb, songId: string, now: string): Promise<void> {
  await db.transaction(
    'rw',
    [db.songs, db.arrangements, db.revisions, db.drafts, db.practice, db.meta],
    async () => {
      const arrangements = await db.arrangements.where('songId').equals(songId).toArray()
      for (const arrangement of arrangements) {
        await db.revisions.where('arrangementId').equals(arrangement.id).delete()
        await db.drafts.delete(arrangement.id)
        await db.practice.delete(arrangement.id)
      }
      await db.arrangements.where('songId').equals(songId).delete()
      await db.songs.delete(songId)
      await touch(db, now)
    },
  )
}

export async function saveDraft(db: AtrilDb, draft: EditorDraft): Promise<void> {
  await db.drafts.put(draft)
}

export async function getDraft(
  db: AtrilDb,
  arrangementId: string,
): Promise<EditorDraft | undefined> {
  return db.drafts.get(arrangementId)
}

export async function clearDraft(db: AtrilDb, arrangementId: string): Promise<void> {
  await db.drafts.delete(arrangementId)
}

export async function getPractice(
  db: AtrilDb,
  arrangementId: string,
): Promise<Practice | undefined> {
  return db.practice.get(arrangementId)
}

/**
 * Cambia la memoria de práctica de un arreglo. No crea revisiones: el tempo de práctica, el
 * fragmento, las notas y el estado no son parte de la ficha (ADR 0003).
 */
export async function updatePractice(
  db: AtrilDb,
  arrangementId: string,
  patch: PracticePatch,
  now: string,
): Promise<Practice> {
  return db.transaction('rw', [db.arrangements, db.practice, db.meta], async () => {
    if (!(await db.arrangements.get(arrangementId))) {
      throw new StorageError('missing', 'El arreglo no existe')
    }
    const next = patchPractice(await db.practice.get(arrangementId), arrangementId, patch, now)
    await db.practice.put(next)
    await touch(db, now)
    return next
  })
}
